// CHAT-PRESENCE (2026-10-29; was CHAT-DE's "Digital employees"): the Chat sidebar's "Team", with
// a DEs | People switch in its header, remembered per person in this browser.
//   DEs     every DE the viewer can talk to (not retired, not in setup), each with its ring
//           (de-presence.tsx) and its hover card; no "DE" pill or "DE ·" prefix — the list is
//           all DEs. Clicking one opens the viewer's DM with it (made on first use, `dms.open`,
//           which also reopens a closed one). Hovered: "Add to #channel" (the open channel, when
//           the viewer may add members and the DE is not in it) and "Open profile".
//   People  every other person in the company with their online dot; clicking opens the DM.
// The DM's unread count sits on the row. No sample rows: none yet says so; a failed DE read says
// so with Retry.
import {
  AvatarTile,
  Button,
  CountPill,
  InfoTip,
  NavList,
  NavRow,
  SidebarCaption,
  SidebarEmpty,
  toast,
  WorkerBadge,
  workerName,
} from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { IdCardIcon, MessageSquareIcon, PlusIcon, UserPlusIcon } from "lucide-react";
import { type ComponentProps, type ReactNode, useState } from "react";
import { keepFocus } from "../../focus/focus";
import { RequestAgentButton, useAgentMode } from "../agents/agent-requests";
import { reachRefusalToast } from "../agents/ask-access";
import {
  type Helper,
  HelperDot,
  HelpersToggle,
  helperCounts,
  helperHref,
  helperWorking,
  useAskTheDe,
  useHelpers,
  useOpenLeads,
} from "../agents/helpers";
import { isManager } from "../company";
import { deLabel } from "../de/de-name";
import { problemText } from "../ops-parts";
import { useAddMember } from "./channel-agents";
import {
  type ChatMember,
  deDms,
  dmMemberIds,
  isDm,
  type MemberIndex,
  shownUnread,
  talkableDes,
} from "./chat-model";
import { type ChannelPrefs, chatKeys, membersQuery, type SidebarChannel } from "./chat-queries";
import {
  DeAvatar,
  DeHoverCard,
  type DePresence,
  RING_WORD,
  ringHint,
  ringTitle,
} from "./de-presence";
import type { StatusIndex } from "./member-status";
import { PresenceDot, presenceState } from "./presence-dot";
import type { ChatContext } from "./use-chat";

/**
 * The one-to-one DE DMs the Team list shows (kept for CHAT-DE's rule): none while the DE list
 * failed, and never a DM with a DE the section does not list (retired, in setup).
 */
export function useDeDmIds(chat: ChatContext, dms: readonly SidebarChannel[]): ReadonlySet<string> {
  return shownDeDmIds(dms, chat.members, chat.meId, chat.deDirectory.failed);
}

/** `useDeDmIds`'s rule. */
export function shownDeDmIds(
  dms: readonly SidebarChannel[],
  members: MemberIndex,
  meId: string | null,
  failed: boolean,
): ReadonlySet<string> {
  if (failed) return new Set();
  const shown = new Set(talkableDes(members).map((m) => m.id));
  return new Set(
    [...deDms(dms, members, meId).entries()].filter(([de]) => shown.has(de)).map(([, c]) => c.id),
  );
}

export type TeamView = "all" | "des" | "people";

const TEAM_LABEL: Record<TeamView, string> = { all: "All", des: "DEs", people: "People" };

const viewKey = (co: string, me: string | null) => `cos.chat.team.${co}.${me ?? "operator"}`;

/** The viewer's last Team view in this browser (All by default). */
export function readTeamView(co: string, me: string | null): TeamView {
  try {
    const v = globalThis.localStorage?.getItem(viewKey(co, me));
    return v === "people" || v === "des" ? v : "all";
  } catch {
    return "all";
  }
}

function writeTeamView(co: string, me: string | null, v: TeamView) {
  try {
    globalThis.localStorage?.setItem(viewKey(co, me), v);
  } catch {
    // Storage off (private window): the choice lasts this visit only.
  }
}

/** The people of the Team list: every other active person, by name. */
export function teamPeople(members: MemberIndex, meId: string | null): ChatMember[] {
  return [...members.values()]
    .filter((m) => m.kind === "person" && m.id !== meId && !m.inactive)
    .sort((a, b) => a.name.localeCompare(b.name));
}

/** The viewer's one-to-one DM with `other`, among the joined DMs. */
function dmWith(dms: readonly SidebarChannel[], meId: string | null, other: string) {
  if (!meId) return undefined;
  return dms.find((c) => {
    if (c.kind !== "dm") return false;
    const ids = dmMemberIds(c);
    return ids.length === 2 && ids.includes(meId) && ids.includes(other);
  });
}

export function TeamSection({
  chat,
  dms,
  prefs,
  channels,
  current,
  present,
  statuses,
  presence,
  ready,
}: {
  chat: ChatContext;
  /** The sidebar's joined DMs (open and closed). */
  dms: readonly SidebarChannel[];
  prefs: ReadonlyMap<string, ChannelPrefs>;
  /** Every listed channel (the open one's `can_invite`). */
  channels: readonly SidebarChannel[];
  current: { channelId?: string; topicId?: string };
  /** Who is here now (presence). */
  present: ReadonlySet<string>;
  /** People's active / away / do-not-disturb. */
  statuses: StatusIndex;
  /** The DE rings by membership id. */
  presence: ReadonlyMap<string, DePresence>;
  /** The channel lists have loaded. */
  ready: boolean;
}) {
  const [view, setView] = useState<TeamView>(() => readTeamView(chat.co, chat.meId));
  const pick = (v: TeamView) => {
    setView(v);
    writeTeamView(chat.co, chat.meId, v);
  };
  const des = talkableDes(chat.members);
  const people = teamPeople(chat.members, chat.meId);
  const byDe = deDms(dms, chat.members, chat.meId);
  const open = channels.find((c) => c.id === current.channelId);
  const target = open && !isDm(open) && !open.archived_at && open.can_invite ? open : undefined;
  const members = useQuery({
    ...membersQuery(chat.api, chat.co, target?.id ?? ""),
    enabled: target !== undefined && view !== "people",
    retry: false,
  });
  const inTarget = members.isSuccess
    ? new Set(members.data.data.map((m) => m.membership_id))
    : null;
  const manager = isManager(chat.company) && chat.meId !== null && !chat.readOnly;
  const mode = useAgentMode(chat.api, chat.company);
  const unreadOf = (dm: SidebarChannel | undefined) => (dm ? shownUnread(dm, prefs.get(dm.id)) : 0);
  // HELPERS: each lead's helpers (count on the row, list on demand; open state per viewer).
  const helpers = useHelpers(chat.api, chat.co).byLead;
  const [openLeads, toggleLead] = useOpenLeads(chat.co, chat.meId);
  const helperProps = (id: string) => ({
    helpers: helpers.get(id) ?? [],
    open: openLeads.has(id),
    onToggle: () => toggleLead(id),
  });
  return (
    <>
      <SidebarCaption
        label="Team"
        action={
          <div className="flex items-center gap-1" data-slot="team-actions">
            <InfoTip label="About the rings">
              <span className="flex flex-col gap-1 text-xs">
                <span>Amber ?: needs you. Red !: stuck.</span>
                <span>Green: working. Dashed: waiting on others.</span>
                <span>Grey: available. Faded: off.</span>
              </span>
            </InfoTip>
            <fieldset
              aria-label="Show in Team"
              data-slot="team-toggle"
              className="m-0 flex items-center rounded-md border border-border p-px"
            >
              {(["all", "des", "people"] as const).map((v) => (
                <button
                  key={v}
                  type="button"
                  aria-pressed={view === v}
                  aria-label={`Team: ${TEAM_LABEL[v]}`}
                  data-team-view={v}
                  onClick={() => pick(v)}
                  className="h-5 rounded-[5px] px-1.5 text-2xs font-medium text-muted-foreground hover:text-foreground aria-pressed:bg-muted aria-pressed:text-foreground"
                >
                  {TEAM_LABEL[v]}
                </button>
              ))}
            </fieldset>
            {manager && view !== "people" && mode.ready && !mode.canAdd ? (
              // MANAGED-POLICY: a managed company asks its agency for a DE.
              <RequestAgentButton api={chat.api} company={chat.company} kind="de" variant="icon" />
            ) : manager && view !== "people" && mode.ready ? (
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label="Add digital employee"
                title="Add digital employee"
                className="text-faint hover:text-foreground"
                nativeButton={false}
                render={
                  <Link
                    to="/c/$slug/setup/des/new"
                    params={{ slug: chat.company.slug }}
                    search={keepFocus as never}
                  />
                }
              >
                <PlusIcon />
              </Button>
            ) : null}
          </div>
        }
      />
      {view !== "des" ? (
        people.length ? (
          <NavList data-slot="sidebar-team-people">
            {people.map((m) => {
              const dm = dmWith(dms, chat.meId, m.id);
              return (
                <PersonRow
                  key={m.id}
                  chat={chat}
                  person={m}
                  dm={dm}
                  unread={unreadOf(dm)}
                  active={dm !== undefined && current.channelId === dm.id}
                  status={statuses.get(m.id)}
                  online={present.has(m.id)}
                  {...helperProps(m.id)}
                />
              );
            })}
          </NavList>
        ) : view === "people" && ready && chat.peopleDirectory.complete ? (
          <SidebarEmpty things="other people" />
        ) : null
      ) : null}
      {view === "people" ? null : chat.deDirectory.failed ? (
        <p role="alert" className="flex items-center gap-2 px-4 py-1 text-xs text-muted-foreground">
          Could not load the digital employees.
          <Button type="button" variant="secondary" size="xs" onClick={chat.deDirectory.retry}>
            Retry
          </Button>
        </p>
      ) : des.length ? (
        <NavList data-slot="sidebar-des">
          {des.map((m) => {
            const dm = byDe.get(m.id);
            return (
              <DeRow
                key={m.id}
                chat={chat}
                de={m}
                presence={presence.get(m.id)}
                dm={dm}
                unread={unreadOf(dm)}
                active={dm !== undefined && current.channelId === dm.id}
                label={deLabel(m.name, m.personName)}
                {...helperProps(m.id)}
                addTo={
                  target &&
                  inTarget &&
                  !inTarget.has(m.id) &&
                  m.deStatus !== "paused" &&
                  m.deStatus !== "not_deployed"
                    ? target
                    : undefined
                }
              />
            );
          })}
        </NavList>
      ) : ready && chat.deDirectory.complete ? (
        <SidebarEmpty things="digital employees" />
      ) : null}
    </>
  );
}

/** Opens (or reopens) the viewer's DM with one member and goes there. */
function useOpenDm(chat: ChatContext, other: { id: string; name: string }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: () =>
      chat.api.client.dms.open({ params: { co: chat.co }, body: { membership_ids: [other.id] } }),
    onSuccess: async (c) => {
      await queryClient.invalidateQueries({ queryKey: chatKeys.channels(chat.co) });
      void navigate({
        to: "/c/$slug/chat/$channelId",
        params: { slug: chat.company.slug, channelId: c.id },
        search: keepFocus as never,
      });
    },
    onError: (e) => {
      // REACH-S1: outside its Hears from — the toast offers "Ask for access".
      if (reachRefusalToast(chat.api, chat.co, e)) return;
      toast.add({
        title: `Could not open a message with ${other.name}`,
        description: problemText(e, e.message),
        type: "error",
      });
    },
  });
}

function rowClass(unread: number, extra = "") {
  // Sidebar rows (channels, DMs, Team, topics) share one 14 px size.
  return ["text-base", unread > 0 ? "font-semibold text-foreground" : "", extra]
    .filter(Boolean)
    .join(" ");
}

const unreadPill = (unread: number) =>
  unread > 0 ? <CountPill count={unread} tone="accent" aria-label={`${unread} unread`} /> : null;

function DeRow({
  chat,
  de,
  presence,
  dm,
  unread,
  active,
  addTo,
  label,
  helpers,
  open,
  onToggle,
}: {
  chat: ChatContext;
  de: ChatMember;
  presence: DePresence | undefined;
  dm: SidebarChannel | undefined;
  unread: number;
  active: boolean;
  /** The open channel, when this DE may be added to it from here. */
  addTo: SidebarChannel | undefined;
  /** HELPERS: the row's name ("Maya · Marketing Director" when the company names by person). */
  label: string;
} & HelperRowProps) {
  const name = workerName(de.name);
  const counts = helperCounts(helpers);
  const canWrite = chat.meId !== null && !chat.readOnly;
  const slug = chat.company.slug;
  const openDm = useOpenDm(chat, { id: de.id, name });
  const add = useAddMember(chat, addTo ?? { id: "", name: "" });
  // DE-FOLLOWUPS: a not-deployed DE is off, like a paused one.
  const ring =
    presence?.state ??
    (de.deStatus === "paused" || de.deStatus === "not_deployed" ? "off" : undefined);
  const row = {
    lead: <DeAvatar name={name} state={ring} hint={ringHint(presence)} />,
    label,
    active,
    title: ring ? `${label} · ${ringTitle(ring, ringHint(presence))}` : label,
    "data-de-row": de.id,
    "data-unread": unread > 0 ? "true" : undefined,
    className: rowClass(unread, ring === "off" ? "text-muted-foreground" : ""),
    badge: ring ? <span className="sr-only">, {RING_WORD[ring]}</span> : undefined,
    trailing: withHelperSpace(unreadPill(unread), counts.total),
  };
  return (
    <li data-slot="nav-row" data-de-item={de.id} className="group/de relative">
      <DeHoverCard
        api={chat.api}
        co={chat.co}
        slug={slug}
        de={{ id: de.id, name }}
        presence={presence}
        canAct={canWrite}
        helpers={counts}
        render={<div />}
      >
        <ul className="m-0 flex list-none flex-col gap-px p-0">
          {dm ? (
            <NavRow
              {...row}
              render={
                <Link
                  to="/c/$slug/chat/$channelId"
                  params={{ slug, channelId: dm.id }}
                  search={keepFocus}
                  activeOptions={{ includeSearch: false, exact: true }}
                />
              }
            />
          ) : (
            <NavRow
              {...row}
              className={`${row.className} w-[calc(100%-1rem)] text-left`}
              render={
                <button
                  type="button"
                  aria-label={`Message ${name}`}
                  disabled={!canWrite || openDm.isPending}
                  onClick={() => openDm.mutate()}
                />
              }
            />
          )}
        </ul>
      </DeHoverCard>
      <HelpersToggle
        lead={label}
        counts={counts}
        open={open}
        onToggle={onToggle}
        className={`absolute ${ROW_MID} right-1 z-10 -translate-y-1/2`}
      />
      <div
        data-slot="de-row-actions"
        className={`absolute ${ROW_MID} ${counts.total ? "right-12" : "right-1"} flex -translate-y-1/2 items-center gap-px rounded-md bg-sidebar/95 opacity-0 group-hover/de:opacity-100 focus-within:opacity-100 pointer-coarse:opacity-100`}
      >
        {addTo ? (
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={`Add ${name} to #${addTo.name}`}
            title={`Add to #${addTo.name}`}
            className="text-faint hover:text-foreground"
            disabled={add.isPending}
            onClick={() => add.mutate(de.id)}
          >
            <UserPlusIcon />
          </Button>
        ) : null}
        <Button
          variant="ghost"
          size="icon-xs"
          aria-label={`Open ${name}'s profile`}
          title="Open profile"
          className="text-faint hover:text-foreground"
          nativeButton={false}
          render={
            <Link to="/c/$slug/agents/de/$id" params={{ slug, id: de.id }} search={keepFocus} />
          }
        >
          <IdCardIcon />
        </Button>
      </div>
      {open && counts.total ? (
        <HelperList chat={chat} lead={{ id: de.id, name, kind: "de" }} helpers={helpers} />
      ) : null}
    </li>
  );
}

/** The vertical middle of a sidebar row (min-h-9; 44 px on touch), for the row's overlays. */
const ROW_MID = "top-[18px] pointer-coarse:top-[22px]";

/** HELPERS: a lead's helpers and its open state (the Team list's rows). */
type HelperRowProps = {
  helpers: readonly Helper[];
  open: boolean;
  onToggle: () => void;
};

/** The row's trailing content, with room for the helper toggle laid over its right end. */
function withHelperSpace(trailing: ReactNode, helpers: number): ReactNode {
  if (!helpers) return trailing;
  return (
    <>
      {trailing}
      <span aria-hidden="true" className="w-8 shrink-0" />
    </>
  );
}

/**
 * The open list under a lead: one 13 px line per helper (AG badge, name, working / idle dot),
 * each opening the DE page's Helpers at it; "Ask the DE" as its one hover icon (a DE lead only).
 * Never a DM, an unread count or "Add to #channel".
 */
function HelperList({
  chat,
  lead,
  helpers,
}: {
  chat: ChatContext;
  lead: { id: string; name: string; kind: "de" | "person" };
  helpers: readonly Helper[];
}) {
  const ask = useAskTheDe(chat.api, chat.company, chat.meId);
  const canAsk = lead.kind === "de" && chat.meId !== null && !chat.readOnly;
  const now = Date.now();
  return (
    <ul
      data-slot="sidebar-helpers"
      aria-label={`${lead.name}'s helpers`}
      className="m-0 mb-1 flex list-none flex-col p-0 pl-9"
    >
      {helpers.map((a) => {
        const name = workerName(a.name);
        const on = helperWorking(a, now);
        return (
          <li key={a.id} data-sidebar-helper={a.id} className="group/helper relative">
            <Link
              {...(helperHref(chat.company, a) as object as ComponentProps<typeof Link>)}
              className="mr-2 flex min-h-7 items-center gap-1.5 rounded-md px-2 text-[13px] text-sidebar-foreground hover:bg-muted hover:text-foreground"
              title={`${name} · ${on ? "Working" : "Idle"}`}
            >
              <WorkerBadge kind="ag" size="sm" />
              <span className="min-w-0 flex-1 truncate">{name}</span>
              <HelperDot working={on} />
            </Link>
            {canAsk ? (
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`Ask ${lead.name} about ${name}`}
                title="Ask the DE"
                className="absolute top-1/2 right-3 -translate-y-1/2 bg-sidebar/95 text-faint opacity-0 group-hover/helper:opacity-100 focus-visible:opacity-100 hover:text-foreground pointer-coarse:opacity-100"
                disabled={ask.isPending}
                onClick={() => ask.mutate({ deId: lead.id, helperName: a.name })}
              >
                <MessageSquareIcon />
              </Button>
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function PersonRow({
  chat,
  person,
  dm,
  unread,
  active,
  status,
  online,
  helpers,
  open,
  onToggle,
}: {
  chat: ChatContext;
  person: ChatMember;
  dm: SidebarChannel | undefined;
  unread: number;
  active: boolean;
  status: Parameters<typeof presenceState>[0] | undefined;
  online: boolean;
} & HelperRowProps) {
  const counts = helperCounts(helpers);
  const canWrite = chat.meId !== null && !chat.readOnly;
  const slug = chat.company.slug;
  const openDm = useOpenDm(chat, { id: person.id, name: person.name });
  const state = status ? presenceState(status) : online ? "active" : "away";
  const lead = (
    <span className="relative inline-flex">
      <AvatarTile name={person.name} size="xs" aria-hidden="true" />
      <PresenceDot state={state} className="absolute -right-0.5 -bottom-0.5" />
    </span>
  );
  const row = {
    lead,
    label: person.name,
    active,
    "data-team-person-row": person.id,
    "data-presence": state,
    "data-unread": unread > 0 ? "true" : undefined,
    className: rowClass(unread),
    trailing: withHelperSpace(unreadPill(unread), counts.total),
  };
  return (
    <li data-slot="nav-row" data-team-person={person.id} className="relative">
      <HelpersToggle
        lead={person.name}
        counts={counts}
        open={open}
        onToggle={onToggle}
        className={`absolute ${ROW_MID} right-1 z-10 -translate-y-1/2`}
      />
      <ul className="m-0 flex list-none flex-col gap-px p-0">
        {dm ? (
          <NavRow
            {...row}
            render={
              <Link
                to="/c/$slug/chat/$channelId"
                params={{ slug, channelId: dm.id }}
                search={keepFocus}
                activeOptions={{ includeSearch: false, exact: true }}
              />
            }
          />
        ) : (
          <NavRow
            {...row}
            className={`${row.className} w-[calc(100%-1rem)] text-left`}
            render={
              <button
                type="button"
                aria-label={`Message ${person.name}`}
                disabled={!canWrite || openDm.isPending}
                onClick={() => openDm.mutate()}
              />
            }
          />
        )}
      </ul>
      {open && counts.total ? (
        <HelperList
          chat={chat}
          lead={{ id: person.id, name: person.name, kind: "person" }}
          helpers={helpers}
        />
      ) : null}
    </li>
  );
}
